import type { FocusEvent } from 'react';

type PrefetchFn = () => Promise<unknown>;
export type PrefetchTier = 'light' | 'heavy';

const prefetched = new Set<string>();

/** Block PDP prefetch until paint settles or the user interacts. */
const HEAVY_PREFETCH_BLOCK_MS = 2_500;
/** Minimum gap between any route chunk prefetches. */
const PREFETCH_THROTTLE_MS = 2_500;
/** Earliest light nav prefetch after first paint (idle). */
const LIGHT_IDLE_PREFETCH_MS = 4_000;

let pageLoadAt = Date.now();
let lastPrefetchAt = 0;
let pointerMovedSinceLoad = false;
let userInteractedSinceLoad = false;
let sessionInstalled = false;

const HEAVY_ROUTE_PREFIXES = ['/product/'];

function installPrefetchSession(): void {
  if (sessionInstalled || typeof globalThis.window === 'undefined') return;
  sessionInstalled = true;
  pageLoadAt = Date.now();

  const markPointerMove = () => {
    pointerMovedSinceLoad = true;
  };
  const markUserInteract = () => {
    userInteractedSinceLoad = true;
  };

  globalThis.window.addEventListener('pointermove', markPointerMove, {
    passive: true,
  });
  globalThis.window.addEventListener('pointerdown', markUserInteract, {
    capture: true,
    passive: true,
  });
  globalThis.window.addEventListener('keydown', markUserInteract, {
    capture: true,
    passive: true,
  });
}

installPrefetchSession();

/** Strip query/hash and trailing slash for stable lookup keys. */
export function normalizePrefetchPath(path: string): string {
  const withoutQuery = path.split(/[?#]/)[0] ?? path;
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery;
}

export function isHeavyPrefetchPath(path: string): boolean {
  const normalized = normalizePrefetchPath(path);
  return HEAVY_ROUTE_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

function resolvePrefetchTier(path: string, tier?: PrefetchTier): PrefetchTier {
  if (tier) return tier;
  return isHeavyPrefetchPath(path) ? 'heavy' : 'light';
}

function mayPrefetchTier(tier: PrefetchTier): boolean {
  const elapsed = Date.now() - pageLoadAt;

  if (tier === 'heavy') {
    if (userInteractedSinceLoad) return true;
    if (pointerMovedSinceLoad && elapsed >= 400) return true;
    return elapsed >= HEAVY_PREFETCH_BLOCK_MS;
  }

  return userInteractedSinceLoad || elapsed >= 1_000;
}

/**
 * Maps canonical route paths to the same dynamic imports used in App.tsx lazy().
 * Keep paths in sync with router config when adding new high-traffic pages.
 */
const ROUTE_PREFETCH_MAP: Record<string, PrefetchFn> = {
  '/': () => import('../pages/public/Home'),
  '/shop': () => import('../pages/public/ShopProductDirectory'),
  '/shop/designs': () => import('../pages/public/Shop'),
  '/about': () => import('../pages/public/About'),
  '/contact': () => import('../pages/public/Contact'),
  '/track-order': () => import('../pages/public/TrackOrderLookup'),
  '/shipping': () => import('../pages/public/Shipping'),
  '/size-guide': () => import('../pages/public/SizeGuide'),
  '/terms': () => import('../pages/public/legal/Terms'),
  '/privacy': () => import('../pages/public/legal/Privacy'),
  '/refunds': () => import('../pages/public/legal/Refunds'),
  '/cart': () => import('../pages/checkout/Cart'),

  '/seller': () => import('../pages/seller/Dashboard'),
  '/seller/dashboard': () => import('../pages/seller/Dashboard'),
  '/seller/orders': () => import('../pages/seller/Orders'),
  '/seller/designs': () => import('../pages/seller/Products'),
  '/seller/products': () => import('../pages/seller/Products'),
  '/seller/earnings': () => import('../pages/seller/Earnings'),
  '/seller/settings': () => import('../pages/seller/Settings'),
  '/seller/store': () => import('../pages/seller/Store'),
  '/seller/analytics': () => import('../pages/seller/Analytics'),
  '/seller/progress': () => import('../pages/seller/Progress'),

  '/creator-program': () => import('../pages/creator/CreatorProgram'),
  '/creator-program/apply': () => import('../pages/creator/Apply'),
  '/creator/dashboard': () => import('../pages/creator/Dashboard'),
  '/creator/onboarding': () => import('../pages/creator/Onboarding'),

  '/account/dashboard': () => import('../pages/account/Dashboard'),
  '/account/designs': () => import('../pages/account/Designs'),
  '/account/orders': () => import('../pages/account/Orders'),
  '/account/wishlist': () => import('../pages/account/Wishlist'),
  '/account/settings': () => import('../pages/account/Settings'),

  '/owner': () => import('../pages/owner/analytics/OverviewPage'),
  '/owner/dashboard': () => import('../pages/owner/analytics/OverviewPage'),
  '/owner/creator-program/applications': () =>
    import('../pages/owner/creator-program/CreatorApplicationsAdminPage'),
};

const ROUTE_PREFETCH_PREFIXES: Array<{ prefix: string; prefetch: PrefetchFn }> = [
  { prefix: '/seller/orders/', prefetch: () => import('../pages/seller/OrderDetail') },
  { prefix: '/account/orders/', prefetch: () => import('../pages/account/OrderDetail') },
  { prefix: '/product/', prefetch: () => import('../pages/public/ProductDetails') },
  { prefix: '/shop/designs/', prefetch: () => import('../pages/public/Shop') },
];

function resolvePrefetchFn(path: string): PrefetchFn | undefined {
  const exact = ROUTE_PREFETCH_MAP[path];
  if (exact) return exact;

  for (const { prefix, prefetch } of ROUTE_PREFETCH_PREFIXES) {
    if (path.startsWith(prefix)) return prefetch;
  }

  return undefined;
}

function runPrefetch(normalized: string): void {
  if (!normalized || prefetched.has(normalized)) return;

  const prefetch = resolvePrefetchFn(normalized);
  if (!prefetch) return;

  prefetched.add(normalized);
  void prefetch().catch(() => {
    prefetched.delete(normalized);
  });
}

/** Prefetch a route chunk once (idempotent). Respects tier gates + throttle. */
export function prefetchRoute(
  path: string,
  options?: { tier?: PrefetchTier; bypassThrottle?: boolean },
): void {
  const normalized = normalizePrefetchPath(path);
  if (!normalized) return;

  const tier = resolvePrefetchTier(normalized, options?.tier);
  if (!mayPrefetchTier(tier)) return;

  const now = Date.now();
  if (
    !options?.bypassThrottle &&
    now - lastPrefetchAt < PREFETCH_THROTTLE_MS
  ) {
    return;
  }

  lastPrefetchAt = now;
  runPrefetch(normalized);
}

export type RoutePrefetchHandlers = {
  onPointerEnter: () => void;
  onPointerDown: () => void;
  onFocus: (event: FocusEvent) => void;
};

/** Intent-based prefetch for links (nav = light, PDP cards = heavy). */
export function getRoutePrefetchHandlers(
  path: string,
  options?: { tier?: PrefetchTier },
): RoutePrefetchHandlers {
  const tier = resolvePrefetchTier(path, options?.tier);

  const tryPrefetch = () => prefetchRoute(path, { tier });

  return {
    onPointerEnter: tryPrefetch,
    onPointerDown: tryPrefetch,
    onFocus: (event) => {
      if (event.target !== event.currentTarget) return;
      tryPrefetch();
    },
  };
}

/** Heavy PDP/card surfaces — never idle-prefetch; interaction only. */
export function getHeavyRoutePrefetchHandlers(
  path: string,
): RoutePrefetchHandlers {
  return getRoutePrefetchHandlers(path, { tier: 'heavy' });
}

/** Optional idle prefetch for light nav targets (not PDP). */
export function scheduleIdleLightPrefetch(path: string): void {
  if (typeof globalThis.window === 'undefined') return;
  if (isHeavyPrefetchPath(path)) return;

  const normalized = normalizePrefetchPath(path);
  if (!normalized || prefetched.has(normalized)) return;

  const fire = () => {
    if (!mayPrefetchTier('light')) return;
    prefetchRoute(path, { tier: 'light' });
  };

  if ('requestIdleCallback' in globalThis.window) {
    globalThis.window.requestIdleCallback(fire, {
      timeout: LIGHT_IDLE_PREFETCH_MS,
    });
    return;
  }

  globalThis.setTimeout(fire, LIGHT_IDLE_PREFETCH_MS);
}

/** Test helpers */
export function resetRoutePrefetchSessionForTests(): void {
  pageLoadAt = Date.now();
  lastPrefetchAt = 0;
  pointerMovedSinceLoad = false;
  userInteractedSinceLoad = false;
  prefetched.clear();
}

export function markPointerMovedForTests(): void {
  pointerMovedSinceLoad = true;
}

export function markUserInteractedForTests(): void {
  userInteractedSinceLoad = true;
}

export function advancePrefetchClockForTests(ms: number): void {
  pageLoadAt -= ms;
}
